<script setup lang="ts">
// types
import type { StrapiSiteFooter } from '@ts-strapi/interfaces'
// hooks
import { useSiteFooter } from '@hooks/widgets/site-footer.hook'
// components
import { Link, Logo } from '@brighterly/lib-lumee-ui'
import { SocialButton } from '@UI/Buttons/SocialButton'
import { FooterBottomBar } from '@UI/Footers/FooterBottomBar'
import { FooterLink } from '@UI/Footers/FooterLink'
import { FooterLinkGroup } from '@UI/Footers/FooterLinkGroup'
import { FooterPaymentBadge } from '@UI/Footers/FooterPaymentBadge'
import { FooterReviewBadge } from '@UI/Footers/FooterReviewBadge'
import { FooterTrustSeal } from '@UI/Footers/FooterTrustSeal'

defineOptions({
  name: 'SiteFooter',
})

defineProps<{
  data: StrapiSiteFooter
}>()

const {
  resolveUrl,
} = useSiteFooter()

const scrollIn = {
  initial: { opacity: 0, y: 32 },
  visibleOnce: { opacity: 1, y: 0, transition: { duration: 500 } },
}
</script>

<template>
  <footer
    v-motion="scrollIn"
    class="site-footer"
  >
    <div class="site-footer__container">
      <div class="site-footer__columns">
        <div class="site-footer__column site-footer__column--brand">
          <div class="site-footer__brand">
            <Logo
              color="inverse"
              version="new"
            />
            <p class="site-footer__slogan btly-text-body-l">{{ data.brand.slogan }}</p>
          </div>
          <FooterLinkGroup
            class="site-footer__group site-footer__group--full"
            :title="data.brand.contactsTitle"
          >
            <Link
              color="inverse"
              size="sm"
              :href="`mailto:${data.brand.contactEmail}`"
            >{{ data.brand.contactEmail }}</Link>
          </FooterLinkGroup>
          <FooterLinkGroup
            class="site-footer__group site-footer__group--full"
            :title="data.brand.partnershipTitle"
          >
            <Link
              color="inverse"
              size="sm"
              :href="`mailto:${data.brand.partnershipEmail}`"
            >{{ data.brand.partnershipEmail }}</Link>
          </FooterLinkGroup>
          <FooterLinkGroup
            class="site-footer__group site-footer__socials"
            :title="data.brand.followUsLabel"
            layout="row"
          >
            <SocialButton
              v-for="(social, index) in data.brand.socials"
              :key="social.id ?? index"
              :network="social.network"
              :url="social.url"
            />
          </FooterLinkGroup>
          <FooterLinkGroup
            class="site-footer__group"
            :title="data.reviewedOn.title"
            layout="row"
          >
            <template
              v-for="(item, index) in data.reviewedOn.platforms"
              :key="item.id ?? index"
            >
              <a
                v-if="item.url"
                class="site-footer__review-link"
                :href="item.url"
                target="_blank"
                rel="noopener noreferrer"
              >
                <FooterReviewBadge :platform="item.platform" />
              </a>
              <FooterReviewBadge
                v-else
                :platform="item.platform"
              />
            </template>
          </FooterLinkGroup>
        </div>

        <nav
          class="site-footer__column site-footer__column--company"
          aria-label="Company"
        >
          <FooterLinkGroup
            class="site-footer__group"
            :title="data.companyColumn.title"
          >
            <FooterLink
              v-for="(item, index) in data.companyColumn.links"
              :key="item.id ?? index"
              :label="item.label"
              :url="resolveUrl(item.url)"
              :badge-label="item.badgeLabel ?? ''"
              :disabled="item.disabled"
            />
          </FooterLinkGroup>
          <FooterLinkGroup
            class="site-footer__group"
            :title="data.policiesColumn.title"
          >
            <FooterLink
              v-for="(item, index) in data.policiesColumn.links"
              :key="item.id ?? index"
              :label="item.label"
              :url="resolveUrl(item.url)"
              :badge-label="item.badgeLabel ?? ''"
              :disabled="item.disabled"
            />
          </FooterLinkGroup>
          <FooterLinkGroup
            class="site-footer__group site-footer__badges"
            :title="data.weAccept.title"
            layout="row"
          >
            <FooterPaymentBadge
              v-for="(item, index) in data.weAccept.methods"
              :key="item.id ?? index"
              :method="item.method"
            />
          </FooterLinkGroup>
          <FooterLinkGroup
            class="site-footer__group site-footer__badges"
            :title="data.safeSecure.title"
            layout="row"
          >
            <FooterTrustSeal
              v-for="(item, index) in data.safeSecure.seals"
              :key="item.id ?? index"
              :seal="item.seal"
            />
          </FooterLinkGroup>
        </nav>

        <nav
          class="site-footer__column site-footer__column--tutoring"
          aria-label="Tutoring"
        >
          <FooterLinkGroup
            v-for="(column, columnIndex) in data.tutoringColumns"
            :key="column.id ?? columnIndex"
            class="site-footer__group"
            :title="column.title"
          >
            <FooterLink
              v-for="(item, index) in column.links"
              :key="item.id ?? index"
              :label="item.label"
              :url="resolveUrl(item.url)"
              :badge-label="item.badgeLabel ?? ''"
              :disabled="item.disabled"
            />
          </FooterLinkGroup>
        </nav>

        <nav
          class="site-footer__column site-footer__column--near-me"
          aria-label="Tutors near you"
        >
          <FooterLinkGroup
            class="site-footer__group site-footer__group--full"
            :title="data.nearMeColumn.title"
          >
            <FooterLink
              v-for="(item, index) in data.nearMeColumn.links"
              :key="item.id ?? index"
              :label="item.label"
              :url="resolveUrl(item.url)"
              :badge-label="item.badgeLabel ?? ''"
              :disabled="item.disabled"
            />
          </FooterLinkGroup>
        </nav>

        <nav
          class="site-footer__column site-footer__column--skills"
          aria-label="Skills"
        >
          <FooterLinkGroup
            v-for="(column, columnIndex) in data.skillsColumns"
            :key="column.id ?? columnIndex"
            class="site-footer__group"
            :title="column.title"
          >
            <FooterLink
              v-for="(item, index) in column.links"
              :key="item.id ?? index"
              :label="item.label"
              :url="resolveUrl(item.url)"
              :badge-label="item.badgeLabel ?? ''"
              :disabled="item.disabled"
            />
          </FooterLinkGroup>
        </nav>

        <nav
          class="site-footer__column site-footer__column--resources"
          aria-label="Resources"
        >
          <FooterLinkGroup
            v-for="(column, columnIndex) in data.resourcesColumns"
            :key="column.id ?? columnIndex"
            class="site-footer__group"
            :title="column.title"
          >
            <FooterLink
              v-for="(item, index) in column.links"
              :key="item.id ?? index"
              :label="item.label"
              :url="resolveUrl(item.url)"
              :badge-label="item.badgeLabel ?? ''"
              :disabled="item.disabled"
            />
          </FooterLinkGroup>
        </nav>
      </div>

      <FooterBottomBar
        :copyright="data.bottomBar.copyright"
        :email="data.bottomBar.email"
        :address="data.bottomBar.address"
      />
    </div>
  </footer>
</template>

<style scoped lang="scss" src="@scss/components/Widgets/Footers/SiteFooter.scss" />
